import {
  deleteQueryKeys,
  readOptionalBooleanParam,
  readRepeatedTrimmedParams,
  readTrimmedParam,
  writeOptionalBooleanParam,
  writeRepeatedParams,
  writeTrimmedParam,
} from "@/lib/filterUrlParams";
import type { FilterUrlSyncConfig } from "@/hooks/useFilterUrlSync";
import {
  applyDiaryCalendarFilterPatch,
  diaryCalendarFiltersDefault,
  type DiaryCalendarFilterState,
} from "./diaryEntryUtils";

export const DIARY_CALENDAR_FILTER_PARAM_KEYS = [
  "includeMe",
  "includeAllEvents",
  "userIds",
  "propertyIds",
  "availabilityPropertyIds",
  "diaryDate",
  "createdBy",
] as const;

function calendarDefaults(isNegotiator: boolean): DiaryCalendarFilterState {
  return diaryCalendarFiltersDefault(isNegotiator);
}

function isRealCalendarDay(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const parsed = new Date(year, month - 1, day);
  return parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;
}

/** True when the landing URL carried any calendar-filter query keys. */
export function diaryCalendarFilterParamsPresent(params: URLSearchParams): boolean {
  return DIARY_CALENDAR_FILTER_PARAM_KEYS.some((key) => params.has(key));
}

/** Read only keys explicitly present in the query string. */
export function parseDiaryCalendarFilterFromSearchParams(
  params: URLSearchParams,
): Partial<DiaryCalendarFilterState> {
  const partial: Partial<DiaryCalendarFilterState> = {};
  if (params.has("includeMe")) {
    const includeMe = readOptionalBooleanParam(params, "includeMe");
    if (includeMe !== undefined) partial.includeMe = includeMe;
  }
  if (params.has("includeAllEvents")) {
    const includeAllEvents = readOptionalBooleanParam(params, "includeAllEvents");
    if (includeAllEvents !== undefined) partial.includeAllEvents = includeAllEvents;
  }
  const userIds = readRepeatedTrimmedParams(params, "userIds");
  if (userIds.length > 0) partial.userIds = userIds;
  const propertyIds = readRepeatedTrimmedParams(params, "propertyIds");
  if (propertyIds.length > 0) partial.propertyIds = propertyIds;
  const availabilityPropertyIds = readRepeatedTrimmedParams(params, "availabilityPropertyIds");
  if (availabilityPropertyIds.length > 0) partial.availabilityPropertyIds = availabilityPropertyIds;
  const date = readTrimmedParam(params, "diaryDate");
  if (date && isRealCalendarDay(date)) partial.date = date;
  const createdBy = readTrimmedParam(params, "createdBy")?.toUpperCase();
  if (createdBy === "BRIDGE_AI" || createdBy === "CRM") partial.createdBy = createdBy;
  return partial;
}

/** Merge URL fragments with role defaults and mirror in-UI toggle rules. */
export function resolveDiaryCalendarFilterFromSearchParams(
  params: URLSearchParams,
  isNegotiator: boolean,
): DiaryCalendarFilterState {
  const merged: DiaryCalendarFilterState = {
    ...diaryCalendarFiltersDefault(isNegotiator),
    ...parseDiaryCalendarFilterFromSearchParams(params),
  };
  return applyDiaryCalendarFilterPatch(merged, {});
}

export function applyDiaryCalendarFilterToSearchParams(
  filter: DiaryCalendarFilterState,
  current: URLSearchParams,
  isNegotiator: boolean,
): URLSearchParams {
  const defaults = calendarDefaults(isNegotiator);
  const next = new URLSearchParams(current);
  deleteQueryKeys(next, DIARY_CALENDAR_FILTER_PARAM_KEYS);
  if (filter.includeMe !== defaults.includeMe) {
    writeOptionalBooleanParam(next, "includeMe", filter.includeMe);
  }
  if (filter.includeAllEvents !== defaults.includeAllEvents) {
    writeOptionalBooleanParam(next, "includeAllEvents", filter.includeAllEvents);
  }
  if (filter.userIds.length > 0) writeRepeatedParams(next, "userIds", filter.userIds);
  if (filter.propertyIds.length > 0) writeRepeatedParams(next, "propertyIds", filter.propertyIds);
  if (filter.availabilityPropertyIds.length > 0) {
    writeRepeatedParams(next, "availabilityPropertyIds", filter.availabilityPropertyIds);
  }
  writeTrimmedParam(next, "diaryDate", filter.date);
  writeTrimmedParam(next, "createdBy", filter.createdBy);
  return next;
}

function calendarFilterCanonical(filter: DiaryCalendarFilterState, isNegotiator: boolean): string {
  return applyDiaryCalendarFilterToSearchParams(filter, new URLSearchParams(), isNegotiator).toString();
}

export function diaryCalendarFiltersEquivalent(
  a: DiaryCalendarFilterState,
  b: DiaryCalendarFilterState,
  isNegotiator: boolean,
): boolean {
  return calendarFilterCanonical(a, isNegotiator) === calendarFilterCanonical(b, isNegotiator);
}

export function createDiaryCalendarFilterUrlSyncConfig(
  isNegotiator: boolean,
): FilterUrlSyncConfig<DiaryCalendarFilterState> {
  const clearedFilter = calendarDefaults(isNegotiator);
  return {
    parseFromSearchParams: (params) => resolveDiaryCalendarFilterFromSearchParams(params, isNegotiator),
    applyToSearchParams: (filter, current) =>
      applyDiaryCalendarFilterToSearchParams(filter, current, isNegotiator),
    filtersEquivalent: (a, b) => diaryCalendarFiltersEquivalent(a, b, isNegotiator),
    clearedFilter,
  };
}
